import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { CheckCircle2, AlertTriangle, XCircle, Info, ChevronRight } from "lucide-react";

const ICON = { success: CheckCircle2, warn: AlertTriangle, error: XCircle, info: Info };
const TONE = {
  success: "text-emerald-700",
  warn: "text-amber-700",
  error: "text-rose-700",
  info: "text-neutral-500",
};

/**
 * Compact admin overview widget:
 *   - 5 most recent events with icon, text, relative time
 *   - "Bekijk alle" link to jump to the Activiteit tab
 * Renders nothing when there are no events yet (keeps the overview clean).
 */
export default function AdminOverviewEventsWidget({ onOpenAll }) {
  const { t } = useT();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/admin/events?limit=5")
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setLoaded(true));
  }, []);

  if (loaded && rows.length === 0) return null;

  return (
    <div className="ed-card p-5 mt-8" data-testid="admin-overview-events">
      <div className="flex items-center justify-between mb-4">
        <p className="ed-eyebrow">{t("admOverviewEventsTitle")}</p>
        <button
          data-testid="admin-overview-events-all"
          onClick={onOpenAll}
          className="font-mono text-[0.6rem] uppercase tracking-[0.15em] inline-flex items-center gap-1 text-neutral-600 hover:text-neutral-900 transition-colors"
        >
          {t("admOverviewEventsAll")} <ChevronRight size={12} strokeWidth={1.75} />
        </button>
      </div>
      <ol className="space-y-2.5" data-testid="admin-overview-events-list">
        {rows.map((r, i) => {
          const Icon = ICON[r.level] || Info;
          const tone = TONE[r.level] || TONE.info;
          const ts = r.created_at ? new Date(r.created_at) : null;
          return (
            <li key={r.id || i} className="flex items-center gap-3">
              <Icon size={14} strokeWidth={1.75} className={`shrink-0 ${tone}`} />
              <p className="font-serif text-sm leading-snug flex-1 min-w-0 truncate" title={r.text}>
                {r.text}
              </p>
              <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 shrink-0">
                {formatRelative(ts, t)}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function formatRelative(dt, t) {
  if (!dt) return "—";
  const diff = Date.now() - dt.getTime();
  if (diff < 60_000) return t("admRelJustNow");
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return t("admRelMinAgo").replace("{n}", mins);
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("admRelHourAgo").replace("{n}", hrs);
  const days = Math.floor(hrs / 24);
  return t("admRelDayAgo").replace("{n}", days);
}
